import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { logout } from '../store/authSlice';

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Initialize collapsed state from localStorage, default to false (expanded)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Navigation links - Users is now inside Settings, so removed from here
  const navLinks = [
    { path: '/', label: 'Home', icon: '🏠', requiresAdmin: false },
    { path: '/executive-summary', label: 'Executive', icon: '📊', requiresAdmin: false },
    { path: '/team-summary', label: 'Team', icon: '👥', requiresAdmin: false },
    { path: '/departments', label: 'Departments', icon: '🏢', requiresAdmin: false },
    { path: '/territories', label: 'Territories', icon: '🗺️', requiresAdmin: false },
    { path: '/acquisitions', label: 'M&A', icon: '🤝', requiresAdmin: false },
    { path: '/cost-savings', label: 'Cost Savings', icon: '💰', requiresAdmin: false },
    { path: '/existing-base', label: 'Existing Base', icon: '🔄', requiresAdmin: false },
    { path: '/leads', label: 'Leads', icon: '🎯', requiresAdmin: false },
    { path: '/settings', label: 'Settings', icon: '⚙️', requiresAdmin: true },
  ];

  // Filter navigation links based on user role
  const visibleLinks = navLinks.filter(
    (link) => !link.requiresAdmin || user?.role === 'ADMIN'
  );

  return (
    <aside
      className={`bg-surface border-r border-border flex flex-col transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Header - Logo and Title */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-center'} w-full`}>
            <img
              src="/uniquex_logo.png"
              alt="Unique X Logo"
              className={`transition-all duration-300 object-contain ${isCollapsed ? 'h-8 w-auto' : 'h-14 w-auto'}`}
            />
          </div>
        </div>
        {/* Collapse/Expand Button */}
        <button
          onClick={toggleSidebar}
          className={`mt-3 w-full flex items-center justify-center py-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors ${
            isCollapsed ? 'px-0' : 'px-4'
          }`}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className="text-lg">{isCollapsed ? '»' : '«'}</span>
          {!isCollapsed && <span className="ml-2 text-sm">Collapse</span>}
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {visibleLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-accent bg-opacity-20 text-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                } ${isCollapsed ? 'justify-center' : ''}`}
                title={isCollapsed ? link.label : undefined}
              >
                <span className="text-lg">{link.icon}</span>
                {!isCollapsed && <span className="ml-3">{link.label}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer - Date, User Info, Logout */}
      <div className="p-4 border-t border-border">
        {/* Current Date */}
        <div className={`text-xs text-text-muted mb-3 ${isCollapsed ? 'text-center' : ''}`}>
          {isCollapsed
            ? new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            : new Date().toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
        </div>

        {/* User Info */}
        {user && (
          <div className={`mb-3 ${isCollapsed ? 'text-center' : ''}`}>
            {isCollapsed ? (
              <div
                className="w-8 h-8 mx-auto rounded-full bg-accent bg-opacity-20 flex items-center justify-center text-accent text-sm font-medium"
                title={`${user.fullName} (${user.role})`}
              >
                {user.fullName.charAt(0).toUpperCase()}
              </div>
            ) : (
              <>
                <div className="text-sm font-medium text-text-primary truncate">
                  {user.fullName}
                </div>
                <span className="text-xs px-2 py-0.5 rounded bg-accent bg-opacity-20 text-accent">
                  {user.role}
                </span>
              </>
            )}
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center justify-center py-2 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors ${
            isCollapsed ? 'px-0' : 'px-4'
          }`}
          title="Logout"
        >
          {isCollapsed ? (
            <span className="text-lg">🚪</span>
          ) : (
            'Logout'
          )}
        </button>
      </div>
    </aside>
  );
};
